//Note: Api instance is created once and wechatID can't be altered after creation
import querystring from 'querystring-es3'
import { getCookie, getUnionId, setCookie, setPadiWxId, AUTH_COOKIE_MAX_AGE } from '../components/common/cookie'
import * as config from './config'
import 'isomorphic-fetch'

const API_ROOT = getApiRoot()
const ossHost = 'https://ecards-confirm-apps.oss-cn-beijing.aliyuncs.com'
const CERT_API_ROOT = getCertApiRoot()
// noResult 参数的两种容忍度：
// - true —— 成功体为空或为纯文本确认，非 JSON 一律按成功处理（会员 SI 流程的端点是这种）；
// - NO_RESULT_STRICT —— 成功体必为空。此时「非空且非 JSON」只可能是中间层（WAF/网关/反代）
//   的错误页，当成成功会把「没送达」上报成「已提交」——投诉按时限判责，一次假成功可能直接
//   等于超时判责，故这类端点必须 fail-closed。
const NO_RESULT_STRICT = 'strict'

// ── 登录态自动续期（CHNA-2793）──────────────────────────────────────────────
// 后端 /api/account/refreshToken 允许「验签通过但已过期」的 idToken，因此请求拿到
// expired.token / 401 时先换新 token 再重试一次；换不到（refresh 缓存过 30 天）才把错误交给上层，
// 由上层引导重新登录。刷新请求本身走原生 fetch，避免递归进 fetchWrapper。
const AUTH_EXPIRED_CODES = ['expired.token', 'auth.required']
// padiWxId / wechatToken 都是登录凭证，必须跨浏览器会话保留（原来写的是会话级 cookie，
// 关掉微信/浏览器就掉登录态，是「反复提示登录过期」的另一半原因）。
let refreshTokenInflight = null

const isJwtToken = (token) => !!token && token.length > 50 && token.indexOf('eyJ') === 0

const isAuthExpiredResult = (result) =>
  !!result &&
  !!result.error &&
  (AUTH_EXPIRED_CODES.indexOf(result.error.code) >= 0 || result.error.status === 401)

const refreshLoginToken = async () => {
  if (refreshTokenInflight) return refreshTokenInflight
  const current = getUnionId()
  if (!current) return ''
  refreshTokenInflight = (async () => {
    try {
      const response = await fetch(`${API_ROOT}${config.refreshWechatToken}`, {
        method: 'POST',
        headers: {
          Authorization: current,
          'content-type': 'application/json; charset=utf-8',
        },
        body: '{}',
      })
      if (!response.ok) return ''
      const body = await response.json()
      const fresh = body && (body.idToken || body.token)
      if (!fresh) return ''
      // 邮箱登录：padiWxId 里存的就是 idToken，直接换新；
      // 微信 unionId 形态由服务端回写 padi:wxid:jwt 映射，cookie 保持 unionId 不变。
      if (isJwtToken(current)) {
        setPadiWxId(fresh)
      }
      setCookie('wechatToken', fresh, undefined, undefined, AUTH_COOKIE_MAX_AGE)
      return fresh
    } catch (e) {
      return ''
    }
  })()
  try {
    return await refreshTokenInflight
  } finally {
    refreshTokenInflight = null
  }
}

const withAuthRefresh = async (run) => {
  const result = await run()
  if (!isAuthExpiredResult(result)) return result
  const fresh = await refreshLoginToken()
  if (!fresh) return result
  return run()
}

const fetchWrapper = async (method, endpoint, data, notJson, postFn, cache, noResult) => {
  const language = getCookie('language') === 'tc' ? 'zh_TW' : getCookie('language') === 'en' ? 'en' : 'zh_CN'
  if (method === 'GET' && endpoint.indexOf('getWxJsSign') < 0) data.lang = language
  const url =
    method === 'POST' || method === 'PUT'
      ? `${endpoint}?lang=${language}`
      : `${endpoint}?${querystring.stringify(data)}`
  const execute = async () => {
    const options = {
      method,
      headers: {
        Authorization: getUnionId(),
        'content-type': 'application/json; charset=utf-8',
      },
      cache: cache || 'default',
    }
    if (method === 'POST' || method === 'PUT') {
      options.body = JSON.stringify(data)
    }
    try {
      const response = await fetch(url, options)
      if (!response.ok) {
        const httpError = new Error(`HTTP code: ${response.status}, ${response.statusText}`)
        httpError.status = response.status
        throw httpError
      }
      if (response.status === 204) return { data: true }
      // noResult 只表示「成功时响应体为空、不能盲调 response.json()」，不等于可以跳过错误检查：
      // 后端的业务拒绝统一回 200 + {error}，不读 body 会把失败当成功上报（客服看到绿色成功、平台侧什么都没发生）
      if (noResult) {
        const text = await response.text()
        if (!text || !text.trim()) return { data: true }
        try {
          const body = JSON.parse(text)
          if (body && body.error) return { error: body.error }
          // 合法 JSON 但没带 error：与下面非 noResult 通道的处理保持一致，按成功
          return { data: true }
        } catch (e) {
          // 非 JSON 响应体。两种通道容忍度不同，见 NO_RESULT_STRICT 处的说明：
          // 成功体必为空的端点里，这只能是中间层的错误页，报成功会让客服以为已提交
          if (noResult === NO_RESULT_STRICT) {
            return { error: { clientMsg: '响应异常，请确认操作是否生效后再重试' } }
          }
          return { data: true }
        }
      }
      const data = notJson ? await response.text() : await response.json()
      if (data.error) return { error: data.error }
      return { data: postFn ? postFn(data) : data }
    } catch (error) {
      // 补上 clientMsg：网络/HTTP 错误没有该字段，showAdminError 等调用方会退回兜底文案，丢失真实原因
      if (error && !error.clientMsg) error.clientMsg = error.message || '网络异常'
      return { error }
    }
  }
  return withAuthRefresh(execute)
}

const fetchFileWrapper = async (endpoint, data, notJson, noResult, outPoint) => {
  const { formData, params } = data
  const url = params ? `${endpoint}?${querystring.stringify(params)}` : `${endpoint}`
  const execute = async () => {
    const options = {
      method: 'POST',
      headers: {
        Authorization: outPoint ? getCookie('wechatToken') : getUnionId(),
      },
      body: formData,
    }
    try {
      const response = await fetch(url, options)
      if (response.status === 401) {
        return { error: { status: response.status } }
      }
      if (!response.ok) throw new Error(`HTTP code: ${response.status}, ${response.statusText}`)
      if (noResult) {
        return { data: true }
      } else {
        const data = notJson ? await response.text() : await response.json()
        if (data.error) return { error: data.error }
        return { data }
      }
    } catch (error) {
      return { error }
    }
  }
  return withAuthRefresh(execute)
}

// 批量导入类端点是「双态响应」：全部成功回 JSON 汇总，存在失败行回 xlsx 失败文件流。
// fetchFileWrapper 固定走 response.json()，碰到文件流会解析失败，故单独读 blob 交调用方按 Content-Type 分支。
const fetchFileDualWrapper = async (endpoint, formData) => {
  const execute = async () => {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getUnionId(),
      },
      body: formData,
    }
    try {
      const response = await fetch(endpoint, options)
      if (response.status === 401) return { error: { status: response.status } }
      if (!response.ok) throw new Error(`HTTP code: ${response.status}, ${response.statusText}`)
      return { data: await response.blob() }
    } catch (error) {
      // 补 clientMsg：网络/HTTP 错误没有该字段，showAdminError 会退回兜底文案而丢失真实原因
      if (error && !error.clientMsg) error.clientMsg = error.message || '网络异常'
      return { error }
    }
  }
  return withAuthRefresh(execute)
}

const fetchFileReturnResult = async (endpoint, data) => {
  const { formData } = data
  const options = {
    method: 'POST',
    headers: {
      Authorization: getUnionId(),
    },
    body: formData,
  }
  const url = endpoint
  try {
    const response = await fetch(url, options)
    if (!response.ok) throw new Error(`HTTP code: ${response.status}, ${response.statusText}`)
    const data = {
      credentialId: parseInt(formData.get('key').split('/')[2]),
      url: `files.padi.com.cn/${formData.get('key')}`,
    }
    if (data.error) return { error: data.error }
    return { data }
  } catch (error) {
    return { error }
  }
}

const fetchMultiParams = async (method, endpoint, data, paramsName, params) => {
  const language = getCookie('language') === 'tc' ? 'zh_TW' : getCookie('language') === 'en' ? 'en' : 'zh_CN'
  const url = `${endpoint}?lang=${language}&${paramsName}=${params}`
  const execute = async () => {
    const options = {
      method,
      headers: {
        Authorization: getUnionId(),
        'content-type': 'application/json; charset=utf-8',
      },
    }
    options.body = JSON.stringify(data)
    try {
      const response = await fetch(url, options)
      if (!response.ok) throw new Error(`HTTP code: ${response.status}, ${response.statusText}`)
      const data = await response.json()
      if (data.error) return { error: data.error }
      return { data }
    } catch (error) {
      return { error }
    }
  }
  return withAuthRefresh(execute)
}

export const getData = async (endpoint, data, notJson, postFn, cache) =>
  fetchWrapper('GET', `${API_ROOT}${endpoint}`, data, notJson, postFn, cache)
export const postData = async (endpoint, data, notJson, postFn, cache, noResult) =>
  fetchWrapper('POST', `${API_ROOT}${endpoint}`, data, notJson, postFn, cache, noResult)
const postMulitiParamsData = async (endpoint, data, paramsName, params) =>
  fetchMultiParams('POST', `${API_ROOT}${endpoint}`, data, paramsName, params)
export const putData = async (endpoint, data, notJson, postFn, cache, noResult) =>
  fetchWrapper('PUT', `${API_ROOT}${endpoint}`, data, notJson, postFn, cache, noResult)
export const postFileData = async (endpoint, data, notJson, noResult) =>
  fetchFileWrapper(`${API_ROOT}${endpoint}`, data, notJson, noResult)
const postFileDataOutPoint = async (endpoint, data, notJson, noResult, outPoint) =>
  fetchFileWrapper(`${endpoint}`, data, notJson, noResult, outPoint)

export function getApiRoot() {
  const origin = document.location.origin
  if (origin === config.URL_prod || origin === config.URL_prod_tw) {
    return config.baseURL_prod
  } else {
    return config.baseURL_qa
  }
}

function getCertApiRoot() {
  const origin = document.location.origin
  if (origin === config.URL_prod || origin === config.URL_prod_tw) {
    return config.certURL_prod
  } else {
    return config.certURL_qa
  }
}

// api services
export const fetchEcards = async () => getData(config.ecards, {}, false, false, 'no-cache')

// 已兑免费首卡证书列表（每次进购卡/购物车页拉取，失败降级为继续展示免费标签）
export const fetchRedeemedFreeFirstCards = async () =>
  getData(config.freeFirstCardRedeemed, {}, false, false, 'no-cache')

export const fetchWechatUser = async () => getData(config.wechatUser, { unionid: getUnionId() })

export const getMobileCode = ({ countryCode, mobile }) => postData(config.getMobileCode, { countryCode, mobile }, true)

export const verifyMobileCode = ({ code, countryCode, mobile, email }) =>
  postData(config.verifyMobileCode, {
    code,
    countryCode,
    mobile,
    email,
  })

export const saveProfile = (profile) => postData(config.saveOrUpdateProfile, profile)

export const getEmailCode = (email) => postData(config.getEmailCode, { email })

export const verifyEmailCode = ({ email, code }) => postData(config.verifyEmailCode, { email, code })

export const fetchProfiles = (email) => getData(config.profiles, { email })

//TODO: cleanup
export const cardRequestOptions = (id) =>
  getData(config.cardOptions, { ecardId: id }, false, (data) => {
    const result = { [id]: data }
    return result
  })

export const addToCart = (items) => postData(config.addToCart, items)

export const fetchShoppingCart = () => getData(config.getShoppingCart, {}, false, false, 'no-cache')

export const credentialRequest = (ecardId) => postMulitiParamsData(config.credentialRequest, {}, 'ecardId', ecardId)

export const hasActivityCard = (activityCode) => getData(config.hasActivityCard, { activityCode })

export const checkoutYouzanStatus = () => getData(config.checkoutYouzanStatus, {})

export const getYouzanMobileCode = (mobile) => postData(config.getYouzanMobileCode, { mobile })

export const bindingYouzan = ({ code, mobile }) => postData(config.bindingYouzan, { code, mobile })

export const createEcardOrder = (data) => postData(config.createEcardOrder, data)

export const getMemberRenew = () => getData(config.memberRenew, {})

export const getStoreRenew = () => getData(config.storeRenew, {})

export const getMemberRenewFuiouOrder = (data) => postData(config.getMemberRenewFuiouOrder, data)

export const getStoreRenewFuiouOrder = (data) => postData(config.getStoreRenewFuiouOrder, data)

export const getWechatJSConfig = (url) => getData(config.wechatJSConfigUrl, { url })

export const getInstructorInfo = (instructorNumber, instructorType) =>
  getData(config.instructorInfo, {
    instructorNumber,
    instructorType,
  })

export const getStoreInfo = (storeNumber) => getData(config.storeInfo, { storeNumber })

export const submitDm = (data) => postData(config.submitDm, data)

export const getTextBookSaleList = () => getData(config.textBookSaleList, {})

export const postTextBookCheckout = (data) => postData(config.textBookSaleCheckout, data)

export const getDmDetail = (code) => getData(config.dmDetail, code ? { code } : {})

export const getDmPaymentStatus = (code) => getData(config.dmPaymentStatus, { code })

export const getEFRPublicPaymentStatus = (code) => getData(config.efrPublicPaymentStatus, { code })

export const instructorApplication = ({ page }) => getData(config.instructorApplication, { page })

export const instructorApplicationDetail = ({ code }) => getData(config.instructorApplicationDetail, { code })

export const instructorApproved = (data) => putData(config.instructorApproved, data)

export const searchStudent = (studentNo) => getData(config.searchStudent, { studentNo })

export const getOssSignature = (type) => getData(config.ossSignature, { type })

export const uploadSignature = (formData) => postFileDataOutPoint(ossHost, formData, false, true)

export const uploadSiFile = (formData) => fetchFileReturnResult(ossHost, formData)

export const getSiAppDetail = (code) => getData(config.siAppDetail, code ? { code } : {})

export const getSiPaymentStatus = (code) => getData(config.siPaymentStatus, { code })

export const uploadPhoto = (formData) =>
  postFileDataOutPoint(`${CERT_API_ROOT}${config.uploadPhoto}`, formData, false, false, true)

export const getSiAppList = () => getData(config.siAppList, {})

export const getSiDetailInProcess = ({ code, credentialId }) =>
  getData(config.getDetailInProcress, {
    code,
    credentialId,
  })

export const submitSiInprocess = (data) => postData(config.submitSiInProcress, data, true, false, false, true)

export const getInstructorInfoByNumber = (instructorNumber) =>
  getData(config.intructorInfoByNumber, { instructorNumber })

export const cdSubmitSiApp = (data) => postData(config.cdSubmitSiApp, data)

export const cdSiAppList = () => getData(config.cdSiAppList, {})

export const getCdSiDetail = ({ code, credentialId }) => getData(config.getCdSiDetail, { code, credentialId })

export const reSubmitCdSiApp = (data) => postData(config.reSubmitCdSiApp, data, true, false, false, true)

export const getOssFile = (key) => getData(config.getOssFile, { key }, true)

export const checkPrerequisiteRequirements = () => getData(config.checkPrerequisiteRequirements, {}, true)

export const refreshToken = () => postData(config.refreshWechatToken, {})

export const getOwsiDetail = (code) => getData(config.owsiDetail, code ? { code } : {})

export const getOwsiPaymentStatus = (code) => getData(config.owsiPaymentStatus, { code })

export const getOwsiCdList = ({ page }) => getData(config.owsiCdList, { page })

export const getOwsiCdDetail = ({ code }) => getData(config.owsiCdDetail, { code })

export const owsiCdApproved = (data) => putData(config.owsiCdApproved, data)

export const getStoreInfoWithLevel = (storeNumber) => getData(config.searchStoreWithLevel, { storeNumber })

export const getAdminDmList = (pageData) => getData(config.getApplicationList, pageData)

export const getAdminSiList = (pageData) => getData(config.getSiApplication, pageData)

export const getAdminOwsiList = (pageData) => getData(config.getOwsiApplication, pageData)

export const csDmApproved = (data) => putData(config.dmApproved, data)

export const csSiApproved = (data) => putData(config.siApproved, data)

export const csOwsiApproved = (data) => putData(config.owsiApproved, data)

export const getIeList = () => getData(config.ieList, {})

export const getIeCandidateList = (ieId) => getData(config.ieCandidateList, { ieId })

export const submitIe = (data) => postData(config.submitIe, data)

export const getCollectionDetail = (alias) => getData(config.collectionDetail, { alias })

export const postCollectionTemplate = (data) => postData(config.submitCollectionTemplate, data, true)

export const getCollectionList = () => getData(config.collectionList, {})

export const postCollection = (data) => postData(config.submitCollection, data, true)

export const getAnswersByAlias = (alias) => getData(config.getAnswersByAlias, { alias })
export const agentElearningList = (email) => getData(config.agentElearningList, { email })

export const agentElearningSubmit = (data) => postData(config.agentElearningSubmit, data)
export const agentElearningOrder = (data) => postData(config.agentElearningOrder, data)
export const agentElearningCreateOrder = (data) => postData(config.agentElearningCreateOrder, data)

export const getEfriDetail = (code) => getData(config.efriDetail, code ? { code } : {})

export const getEfriPaymentStatus = (code) => getData(config.efriPaymentStatus, { code })

export const getEfrtList = ({ page }) => getData(config.efrtList, { page })

export const getEFRTDetailByCode = (code) => getData(config.efrtDetailByCode, { code })

export const efriApproved = (data) => putData(config.efriApproved, data)

export const adminEfriAppList = (pageData) => getData(config.adminEfriAppList, pageData)

export const adminEfriApproved = (data) => putData(config.adminEfriApproved, data)

export const collectionEditerDetail = (alias) => getData(config.collectionEditerDetail, { alias })

export const ieListForExaminer = () => getData(config.ieListForExaminer, {})

export const ieDetailListForExaminer = (data) => getData(config.ieDetailListForExaminer, data)

export const downloadExamAttempts = (ieId) => getData(config.downloadExamAttempts, { ieId })

export const uploadExamAttempts = ({ formData, ieId }) =>
  fetchFileReturnResult(getApiRoot() + config.uploadExamAttempts + '&ieId=' + ieId, { formData })

export const adminGetIEScheduleList = (year) => getData(config.ieScheduleList, { year })

export const adminUploadIESchedule = ({ formData, year }) =>
  postFileData(config.ieScheduleUpload, { formData, params: { year } })

export const ieScheduleTemplateUrl = () => `${getApiRoot()}${config.ieScheduleTemplate}`

export const uploadTrainingRecord = (data) => putData(config.uploadTrainingRecord, data)

export const uploadCourseReport = (data) => putData(config.uploadCourseReport, data)

export const basicMermaidDetail = (code) => getData(config.basicMermaidDetail, code ? { code } : {})

export const getBasicMermaidPaymentStatus = (code) => getData(config.basicMermaidPaymentStatus, { code })

export const getBasicMermaidTList = ({ page }) => getData(config.basicMermaidTList, { page })

export const getBasicMermaidTDetail = (code) => getData(config.basicMermaidTDetail, { code })

export const basicMermaidTApproved = (data) => putData(config.basicMermaidTApproved, data)

export const adminBasicMermaidAppList = (pageData) => getData(config.adminBasicMermaidAppList, pageData)

export const basicMermaidCsReview = (data) => putData(config.basicMermaidCsApproved, data)

export const mermaidiDetail = (code) => getData(config.mermaidiDetail, code ? { code } : {})

export const getMermaidiPaymentStatus = (code) => getData(config.mermaidiPaymentStatus, { code })

export const getMermaidTList = ({ page }) => getData(config.mermaidTList, { page })

export const getMermaidTDetail = (code) => getData(config.mermaidTDetail, { code })

export const mermaidTApproved = (data) => putData(config.mermaidTApproved, data)

export const adminMermaidAppList = (pageData) => getData(config.adminMermaidAppList, pageData)

export const mermaidCsReview = (data) => putData(config.mermaidCsApproved, data)

export const getFdiDetail = (code) => getData(config.fdiDetail, code ? { code } : {})

export const getFdiPaymentStatus = (code) => getData(config.fdiPaymentStatus, { code })

export const getFdiTList = ({ page }) => getData(config.fdiTList, { page })

export const getFdiTDetail = (code) => getData(config.fdiTDetail, { code })

export const fdiTApproved = (data) => putData(config.fdiTApproved, data)

export const adminFdiAppList = (pageData) => getData(config.adminFdiAppList, pageData)

export const fdiCsReview = (data) => putData(config.fdiCsApproved, data)

export const getAfdiDetail = (code) => getData(config.afdiDetail, code ? { code } : {})

export const getAfdiPaymentStatus = (code) => getData(config.afdiPaymentStatus, { code })

export const getAfdiTList = ({ page }) => getData(config.afdiTList, { page })

export const getAfdiTDetail = (code) => getData(config.afdiTDetail, { code })

export const afdiTApproved = (data) => putData(config.afdiTApproved, data)

export const adminAfdiAppList = (pageData) => getData(config.adminAfdiAppList, pageData)

export const afdiCsReview = (data) => putData(config.afdiCsApproved, data)

export const getMfdiDetail = (code) => getData(config.mfdiDetail, code ? { code } : {})

export const getMfdiPaymentStatus = (code) => getData(config.mfdiPaymentStatus, { code })

export const getMfdiTList = ({ page }) => getData(config.mfdiTList, { page })

export const getMfdiTDetail = (code) => getData(config.mfdiTDetail, { code })

export const mfdiTApproved = (data) => putData(config.mfdiTApproved, data)

export const adminMfdiAppList = (pageData) => getData(config.adminMfdiAppList, pageData)

export const mfdiCsReview = (data) => putData(config.mfdiCsApproved, data)

export const getAllecardCustomization = () => getData(config.pwInfo, {})

export const getOtherAppsPaymentStatus = (code) => getData(config.otherAppsPaymentStatus, { code })

export const proCheck = (data) => getData(config.procheck, data)

export const getProCheckCaptcha = () => getData(config.procheckCaptcha, {})

export const adminCertMergeList = (pageData) => getData(config.certsMergeList, pageData)

export const adminFindAccountList = (pageData) => getData(config.findAccountList, pageData)

export const adminUpdateUserInfoList = (pageData) => getData(config.adminUpdateUserInfoList, pageData)

export const adminGetAdminWenjuanList = (pageData) => getData(config.adminWenjuanList, pageData)

export const adminGetOtherAppsList = (pageData) => getData(config.adminGetOtherAppsList, pageData)

export const updateOtherAppFileNames = (data) => putData(config.otherAppFileNames, data)

export const adminGetStoreAppsList = (pageData) => getData(config.adminGetStoreAppsList, pageData)

export const getCustomizeEcardUrl = (data) => postData(config.getCustomizeEcardUrl, data)

export const getAdminCustomizedImages = (pageData) => getData(config.getCustomizedImages, pageData)

export const customizedImagesApproved = (data) => putData(config.customizedImageApprove, data)

export const getMemberApplicationGoodMatrix = () => getData(config.memberApplicationGoodList, {})

export const updateMemberApplicationGoodPrice = (data) => putData(config.memberApplicationGoodPrice, data)

export const customizedImage = (ecardId) => getData(config.customizedImage, { ecardId })

export const affiliateIdQueryByEmail = (data) => getData(config.affiliateIdQueryByEmail, data)

export const affiliateIdQueryByAffiliateId = (data) => getData(config.affiliateIdQueryByAffiliateId, data)

// 客服后台验证码查询：成功后是 { code, generatedAt?, expiresAt? }，没查到是空对象（不是错误）
export const verificationCodeCognito = (data) => getData(config.verificationCodeCognito, data)

export const verificationCodeEmail = (data) => getData(config.verificationCodeEmail, data)

export const verificationCodeMobile = (data) => getData(config.verificationCodeMobile, data)

// PADI Club 手机号登录/注册的短信码：码只在 Redis 里，10 分钟过期、登录成功即删 —— 「没查到」是常态
export const verificationCodeClubMobile = (data) => getData(config.verificationCodeClubMobile, data)

export const getElearningAgentOrders = (data) =>
  getData(config.getElearningAgentOrders, {
    email: data.email || '',
    status: data.status || '',
    page: data.page || 0,
    size: data.size || 20,
  })
export const getElearningAgentOrderDetail = (data) =>
  getData(config.getElearningAgentOrderDetail.replace('{id}', data.id), {})
export const retryElearningIssueCode = (data) => postData(config.retryElearningIssueCode.replace('{id}', data.id))

export const adminGetRenewalOrderList = (data) => getData(config.renewalOrderList, data)
export const adminGetRenewalCurrentYear = () => getData(config.renewalPriceCurrentYear, {})

export const adminConfirmRenewalOrder = (data) => putData(config.renewalOrderConfirm, data, false, false, false, true)

export const adminGetStoreDsdList = (data) => getData(config.storeDsdList, data)

export const adminGetDsdDashboardSummary = (data) => getData(config.dsdDashboardSummary, data)

export const adminGetDsdDashboardStoreStats = (data) => getData(config.dsdDashboardStoreStats, data)

// 刻意不收参数：该接口恒为全量累计（endDate 缺省 = 今天，由后端定），与看板日期筛选无关。
// 想让它跟筛选联动必须先改这里的签名 —— 那一步会在 code review 里被看到。
export const adminGetDsdDashboardTotalSummary = () => getData(config.dsdDashboardTotalSummary, {})

export const adminGetDsdStoreConfigList = () => getData(config.dsdStoreConfigList, {})

export const adminCreateDsdStoreConfig = (data) => postData(config.dsdStoreConfigCreate, data)

export const adminUpdateDsdStoreConfig = (id, data) => putData(config.dsdStoreConfigUpdate.replace('{id}', id), data)

export const adminUploadDsdStoreImage = (formData) => postFileData(config.dsdStoreConfigUploadImage, { formData })

// CHNA-2689 课程总监/教练训练官名录管理
export const adminGetProDirectoryList = () => getData(config.backstageProDirectoryList, {})

export const adminUpsertProDirectory = (data) => postData(config.backstageProDirectoryUpsert, data)

// 删除接口成功后返回空 body，传 noResult 跳过 JSON 解析，避免误报"删除失败"
export const adminDeleteProDirectory = (data) =>
  postData(config.backstageProDirectoryDelete, data, false, undefined, undefined, true)

export const adminUploadProDirectoryImage = (formData) =>
  postFileData(config.backstageProDirectoryUploadImage, { formData })

export const adminQueryProDirectoryInstructor = (memberNumber) =>
  getData(config.backstageProDirectoryQueryInstructor, { memberNumber })

// ==================== 教材商品管理 ====================

export const adminGetTextbookGoodsList = (data) => getData(config.adminTextbookGoodsList, data)

export const adminImportTextbookGoods = (formData) => postFileData(config.adminTextbookGoodsImport, { formData })

// ==================== 教材订单管理 ====================

export const adminGetTextbookOrderList = (data) => getData(config.adminTextbookOrderList, data)

// 发货邮件审计（只读）。按订单反查该单的发货邮件投递结果，新的在前。
// 列表不含邮件正文——正文体量大（HTML）且只在点开时才需要，故另走 body 端点按需取。
export const adminGetTextbookOrderEmailAudit = (orderId) =>
  getData(config.adminTextbookOrderEmailAudit.replace('{id}', encodeURIComponent(orderId)), {})

// 正文必须带订单号一起传：正文未脱敏，后端按「订单号 + 审计 ID + 业务类型」三键查，
// 保证翻不出其他业务的邮件正文
export const adminGetTextbookOrderEmailAuditBody = (orderId, auditId) =>
  getData(
    config.adminTextbookOrderEmailAuditBody
      .replace('{id}', encodeURIComponent(orderId))
      .replace('{auditId}', encodeURIComponent(auditId)),
    {}
  )

// ==================== 卡面订单批量发货 ====================

// 响应体是 JSON（导入汇总或业务错误）而非 xlsx 文件流。
// 文件类端点鉴权/业务失败时后端回 200 + JSON，调用 blob 下载前必须先用它分辨，否则会把错误报文存成 xlsx。
export const isJsonBlob = (blob) =>
  !!blob && typeof blob.type === 'string' && blob.type.indexOf('application/json') >= 0

// 批量发货导入（POST /ship/import）。后端双态响应归一为三种结果：
//   { data: { kind: 'summary', summary } }  全部成功/跳过，summary = { total, success, skipped, failed, failures }
//   { data: { kind: 'errorFile', blob } }   存在失败行，blob 为比模板多「失败原因」列的 xlsx
//   { error }                               业务错误（200 + {error}）或网络异常
export const adminImportEcardShip = async (formData) => {
  const result = await fetchFileDualWrapper(`${API_ROOT}${config.ecardOrderShipImport}`, formData)
  if (result.error) return result
  const blob = result.data
  if (!isJsonBlob(blob)) return { data: { kind: 'errorFile', blob } }
  try {
    const payload = JSON.parse(await blob.text())
    if (payload.error) return { error: payload.error }
    return { data: { kind: 'summary', summary: payload } }
  } catch (e) {
    return { error: { clientMsg: '导入响应解析失败，请稍后重试' } }
  }
}

// ==================== 富友投诉处理（客诉，CHNA-2752） ====================
// 落点 /api/backstage/fuiou/complaints/**，由 SysAuthCSInterceptor 校验客服身份并注入操作人邮箱

const complaintUrl = (endpoint, complaintId) => endpoint.replace('{complaintId}', encodeURIComponent(complaintId))

export const adminGetFuiouComplaintList = (data) => getData(config.fuiouComplaintList, data)

// 各本地处理态数量，供列表页角标。返回 { PENDING: n, PROCESSING: n, ... }
export const adminGetFuiouComplaintSummary = () => getData(config.fuiouComplaintSummary, {})

// refresh=true 时会先打富友拉最新（1 次详情 + 最多 20 页协商历史），只用于客服手动点「刷新」；
// 带副作用的 GET 不能当默认，否则每次打开详情都放大成多次外部调用
export const adminGetFuiouComplaintDetail = (complaintId, refresh) =>
  getData(complaintUrl(config.fuiouComplaintDetail, complaintId), refresh ? { refresh: true } : {})

// 按日期区间补拉投诉单（补回调/轮询丢失），返回 { synced, truncated, failedChannels, message }。
// failedChannels 是「缺省同步下单渠道整段失败」的显式回报：只读 message 的话，
// 「另一个渠道挂了」与「那一半刚好没有投诉」在界面上不可区分（见 SyncModal）。
// 后端用 @RequestParam 收参，而 postData 在 POST 分支会把 `?lang=` 无条件追加到 URL 末尾——
// 拼在已有 query 后面会把最后一个参数值污染成 "...?lang=zh_CN"，后端拿到非法日期直接 400。
// 故这里自己拼 query（同 fetchFileWrapper 的自建 fetch 先例）。
export const adminSyncFuiouComplaint = async ({ beginDate, endDate, channel }) => {
  const query = querystring.stringify({ beginDate, endDate, ...(channel ? { channel } : {}) })
  const options = { method: 'POST', headers: { Authorization: getUnionId() } }
  try {
    const response = await fetch(`${API_ROOT}${config.fuiouComplaintSync}?${query}`, options)
    if (!response.ok) throw new Error(`HTTP code: ${response.status}, ${response.statusText}`)
    const data = await response.json()
    if (data.error) return { error: data.error }
    return { data }
  } catch (error) {
    if (error && !error.clientMsg) error.clientMsg = error.message || '网络异常'
    return { error }
  }
}

// 四个写入口在后端都返回 void（200 + 空 body）。传 noResult 是为了两件事：
// 1. 成功时对空 body 调 response.json() 会抛 SyntaxError，被 fetchWrapper 的 catch 收成 { error }——
//    操作其实成功了，客服看到的却是「Unexpected end of JSON input」，然后重复提交一次；
// 2. fetchWrapper 的 noResult 分支会先读 body——后端业务拒绝（单已终态/并发锁/校验失败）统一回
//    200 + {error}，不读就会把失败当成功弹绿字。
// 这里用 NO_RESULT_STRICT 而非 true：成功体必空，故「非空且非 JSON」是异常而非纯文本确认。
// 会员 SI 流程共用该分支但成功体就是纯文本，收紧只限本通道，不要动共享分支。
const postNoResult = (endpoint, data) => postData(endpoint, data, false, undefined, undefined, NO_RESULT_STRICT)

export const adminReplyFuiouComplaint = (complaintId, data) =>
  postNoResult(complaintUrl(config.fuiouComplaintReply, complaintId), data)

export const adminCompleteFuiouComplaint = (complaintId, data) =>
  postNoResult(complaintUrl(config.fuiouComplaintComplete, complaintId), data)

export const adminRejectFuiouComplaintRefund = (complaintId, data) =>
  postNoResult(complaintUrl(config.fuiouComplaintRefundReject, complaintId), data)

export const adminFinishFuiouComplaintAlipay = (complaintId, data) =>
  postNoResult(complaintUrl(config.fuiouComplaintAlipayFinish, complaintId), data)

// 「同意退款」走的是既有的富友退款链路（投诉审批接口只有 REJECT 一个动作）。
// businessType 必须与原支付单一致，故调用方要先用下面的订单详情取到它。
export const adminGetFuiouOrderDetail = (orderId) =>
  getData(config.fuiouPaymentOrderDetail.replace('{orderId}', encodeURIComponent(orderId)), {})

export const adminRefundFuiouOrder = (orderId, data) =>
  postData(config.fuiouPaymentOrderRefund.replace('{orderId}', encodeURIComponent(orderId)), data)

// 举证图片上传 → { mediaId }，回填到回复的 images 或拒绝退款的 rejectMediaList
export const adminUploadFuiouComplaintImage = (formData) => postFileData(config.fuiouComplaintImages, { formData })

// 投诉材料图片。后端带商户凭证向富友换字节（凭证不能下发到浏览器），并把该地址与本单的
// 材料/协商历史比对后才放行，故 complaintId 是必填的第二个参数。
// 不走 getData：通用 GET 包装只解析 JSON/text，会把图片字节读成乱码。
export const adminDownloadFuiouComplaintImage = async (complaintId, mediaUrl) => {
  const query = querystring.stringify({ complaintId, mediaUrl })
  try {
    const response = await fetch(`${API_ROOT}${config.fuiouComplaintImages}?${query}`, {
      method: 'GET',
      headers: { Authorization: getUnionId() },
    })
    if (!response.ok) throw new Error(`HTTP code: ${response.status}, ${response.statusText}`)
    // 文件类端点鉴权/业务失败时后端回 200 + JSON 错误体（地址不在本单材料清单里等），
    // 不分 Content-Type 会把错误报文当图片字节，渲染成一张解释不了原因的破图
    const contentType = (response.headers.get('content-type') || '').toLowerCase()
    if (contentType.indexOf('application/json') >= 0) {
      try {
        const payload = await response.json()
        if (payload && payload.error) return { error: payload.error }
      } catch (e) {
        // 落到兜底错误
      }
      return { error: { clientMsg: '图片获取失败' } }
    }
    return { data: await response.blob() }
  } catch (error) {
    if (error && !error.clientMsg) error.clientMsg = error.message || '网络异常'
    return { error }
  }
}
